'use client'

import React, {createContext, useCallback, useContext, useEffect, useState} from 'react'
import Cookies from 'universal-cookie'
import {initializeApp} from 'firebase/app'
import {getMessaging, getToken, onMessage} from 'firebase/messaging'
import {useQueryClient} from '@tanstack/react-query'
import {closeAllTabs, loginSuccess, setColumnsSettings, setConstants, setLayout, setNavigationBadges, setRole, setUser, signOutSuccess, toggleColumn, useAppDispatch, useAppSelector} from '@/store'
import {io} from 'socket.io-client'
import {config, PERSIST_STORE_NAME, SOCKET_URL} from '@/constants/app.constant'
import useUser from '@/utils/hooks/useUser'
import {logout as logoutApi} from '@/services/apiAuth'
import {useNavigate} from 'react-router-dom'
import {checkPermissionsByUser, showNotification} from '@/utils/helpers'
import {updateFCMToken} from '@/services/apiUsers'
import {Notification, toast} from '@/components/ui/index'
import useAutoLogout from "@/utils/hooks/useAutoLogout";
import useAutoLogoutOnTabClose from '@/utils/hooks/useAutoLogoutOnTabClose'
import {Button} from "@/components/ui/Button";
import Dropdown from "@/components/ui/Dropdown";
import Checkbox from "@/components/ui/Checkbox/Checkbox";
import {LuColumns3} from 'react-icons/lu'

// import {COOKIE_DOMAIN} from "../../constants/app.constant";

const SessionContext = createContext()

const firebaseConfig = {
    apiKey: 'AIzaSyDU3FTx5vjZSQb9xCQIuqHdtdw5M_yoyQA',
    authDomain: 'tachad-c5ff1.firebaseapp.com',
    projectId: 'tachad-c5ff1',
    storageBucket: 'tachad-c5ff1.firebasestorage.app',
    messagingSenderId: '169506683475',
    appId: '1:169506683475:web:d8e0e4c1499c0fa36d24a6',
    measurementId: 'G-9M0GHZ8PLW',
    fcmPublicKey: 'BAcTSE7TdG1fzVziYEl_KkvyTvXs68BiMTVQL4T2zOqw1OLM9ro8OSMlz6KaKdktvCwOvSRu4bb_8ehXwAz-MVY'
}

export function updateToken(messaging) {
    if ('Notification' in window) {
        if (window.Notification.permission === 'granted') {
            getToken(messaging, {vapidKey: firebaseConfig.fcmPublicKey}).then((fcmToken) => {
                let currentFCMToken = sessionData('fcmToken')

                if (fcmToken) {
                    if (fcmToken != currentFCMToken)
                        updateFCMToken({token: fcmToken}).then(res => {
                            setSessionData('fcmToken', fcmToken)
                        })
                } else {
                    // Show permission request UI
                    console.log('No registration token available. Request permission to generate one.')
                    // ...
                }
            }).catch((err) => {
                console.log('An error occurred while retrieving token. ', err)
                // ...
            })
        } else if (window.Notification.permission !== 'granted') {
            window.Notification.requestPermission().then(permission => {
                if (permission === 'granted') {
                    updateToken(messaging)
                }
            })
        }
    }
}

export function initFirebase(dispatch, queryClient) {
    const sessionId = getSessionId()

    if (typeof window !== 'undefined') {
        // Initialize Firebase
        const firebaseApp = initializeApp(firebaseConfig)
        // const analytics = getAnalytics(firebaseApp);
        const messaging = getMessaging(firebaseApp)

        if (sessionId)
            updateToken(messaging, sessionId)

        onMessage(messaging, (payload) => {
            console.log('fcm fg onMessage:', payload)

            let action = payload?.data?.action
            if (action) {
                action = action.split(':')[0]
                const actionPayload = action.slice(-1)[0]

                queryClient.invalidateQueries(['notifications'])
                /*if (action === 'play') {
                    dispatch(resume());
                } else if (action === 'pause') {
                    dispatch(pause());
                } else if (action === 'playlist') {
                    queryClient.invalidateQueries(["playlist", actionPayload]);
                } else if (action === 'ticket') {
                    queryClient.invalidateQueries(["tickets"]);
                    queryClient.invalidateQueries(["ticket", actionPayload]);
                }*/
            }

            showNotification({
                title: payload?.notification?.title,
                body: payload?.notification?.body,
                image: payload?.notification?.image,
                icon: payload?.notification?.icon,
                data: payload?.data
            })
        })
    }
}

export function sessionData(key, defaultValue) {
    let session = {}
    const cookies = new Cookies()

    try {
        session = cookies.get(PERSIST_STORE_NAME) || '{}'
        if (typeof session != 'object') session = JSON.parse(session)
    } catch (e) {
        console.error(e)
        return defaultValue
    }

    // alert(session[key])

    return session[key] || defaultValue
}

export function setSessionData(key, value) {
    const cookies = new Cookies()
    let session = {}

    try {
        session = cookies.get(PERSIST_STORE_NAME) || '{}'
        if (typeof session != 'object') session = JSON.parse(session)
    } catch (e) {
        console.error(e)
    }

    session[key] = value

    cookies.set(PERSIST_STORE_NAME, JSON.stringify(session), {
        path: '/',
        // domain: COOKIE_DOMAIN,
        expires: new Date(
            new Date().getTime() + 10 * 365 * 24 * 60 * 60 * 1000
        )
    })
}

export function getSessionId() {
    return sessionData('token')
}

function SessionProvider({children}) {
    // let [sessionId, setSessionId] = useState(getSessionId())
    const dispatch = useAppDispatch()
    const queryClient = useQueryClient()
    const {accountData, user, role, isLoading, error: userError} = useUser()
    const {signedIn, token: sessionId} = useAppSelector(state => state.auth.session)
    const navigate = useNavigate()

    // console.log('userError:', userError)

    const [eventListeners, setEventListeners] = useState({})

    const addEventListener = useCallback((action, name, listener) => {
        let newEventListeners = eventListeners || {}
        newEventListeners[action] = newEventListeners[action] || {}
        newEventListeners[action][name] = listener

        setEventListeners(newEventListeners)

        // setEventListeners(eventListeners=>({...eventListeners, [action]: {...eventListeners?.[action] || {}, [name]: {...eventListeners?.[action]?.[name] || {}, [name]: listener}}}))
    }, [eventListeners])

    const removeEventListener = useCallback((action, name) => {
        delete eventListeners?.[action]?.[name]
        setEventListeners(eventListeners)
    }, [eventListeners])

    const fireEvent = useCallback((action, data) => {
        const listeners = eventListeners?.[action] || {}
        for (let eventName of Object.keys(listeners)) {
            console.log(`Firing WS Event ${action} (${eventName})...`)
            listeners[eventName]?.(data)
        }
    }, [eventListeners])

    useEffect(() => {
        // console.log('isLoading:',isLoading, 'accountData:', accountData, 'signedIn:', signedIn, 'user:',user)
        if (!isLoading && signedIn) {
            const {newTicketsMessages = 0, newEmployeesRelatives = 0, newReservations = 0, newHotelsReservations = 0, newToursReservations = 0, newAccommodationsReservations = 0, config: config2} = accountData || {}

            dispatch(setNavigationBadges({
                support: newTicketsMessages,
                tickets: newTicketsMessages,
                'employees/menu': newEmployeesRelatives,
                'employees/relatives': newEmployeesRelatives,
                reservations: newReservations,
                'travel': newHotelsReservations + newToursReservations + newAccommodationsReservations,
                'hotels/menu': newHotelsReservations,
                'hotels/reservations': newHotelsReservations,
                'tours/menu': newToursReservations,
                'tours/reservations': newToursReservations,
                'accommodations/menu': newAccommodationsReservations,
                'accommodations/reservations': newAccommodationsReservations,
            }))
            // console.log(isLoading, accountData, signedIn, user, role)

            for (let key of Object.keys(config2 || {})) {
                // if (config?.[key]) { // first delete the set
                //     for (let key2 in config?.[key])
                //         delete config[key]?.[key2];
                // }

                config[key] = config2?.[key]
            }

            if (user) {
                dispatch(setConstants(config))
                dispatch(setLayout(role.layoutType || 2))
                dispatch(loginSuccess())
                dispatch(setUser(user))
                dispatch(setRole(role))
            } else {
                if (userError)
                    return toast.push(
                        <Notification
                            title={"در حال حاضر امکان ارتباط با سرور وجود ندارد!"}
                            type={"danger"}
                        />
                    )
                logoutApi({}).finally(() => {
                    dispatch(signOutSuccess())
                    dispatch(closeAllTabs())
                    dispatch(setUser(null))
                    // setSessionId(null)
                    queryClient.removeQueries()
                    setTimeout(() => {
                        navigate('/login', {replace: true})
                    }, 500)
                })
            }
        }
    }, [isLoading, signedIn, accountData, user, role])

    useEffect(() => {
        if (window?.navigator && 'serviceWorker' in navigator) {
            navigator.serviceWorker
                .register('/service-worker.js')
                .then((registration) => {
                })
                .catch((error) => {
                    console.error(error)
                })

            /*navigator.serviceWorker.addEventListener('message', event => {
                console.log('event:', event)

                const action = event.data?.data?.action;
                const link = event.data?.data?.link;

                if (link)
                    window.open(link);


            });*/
        }

        if (window.location.protocol == "https:")
            initFirebase(dispatch, queryClient)
    }, [dispatch, queryClient])

    const [socket, setSocket] = useState(null)

    useEffect(() => {
        const socketInstance = io(SOCKET_URL, {
            auth: {
                sessionId: getSessionId()
            },
            transports: ['websocket']
        })

        setSocket(socketInstance)

        socketInstance.onAny((eventName, ...args) => {
            // console.log(`Event received: ${eventName}`, args);

            fireEvent(eventName, ...args)
        })

        socketInstance.on('notification', (data) => {
            toast.push(
                <Notification
                    title={data.message}
                    type={data.type}
                />
            )
        })

        return () => {
            socketInstance.disconnect()
        }
    }, [user])

    // const userPermissions = useAppSelector(state => state.auth.user.permissions || []);
    const rolePermissions = useAppSelector(state => state.auth.role?.permissions || [])

    const hasPermission = useCallback((permissions, requireAll = true) => {
        return checkPermissionsByUser(permissions, rolePermissions, requireAll)
    }, [rolePermissions])

    // socket.close();

    const getTableColumns = ({fields}) => {
        let columns = []
        for (let column of Object.values(fields).filter(field => field && field.column && hasPermission(field.permissions, field.hasOwnProperty('requireAllPermissions') ? field.requireAllPermissions : true))) {
            let subcolumns = [{
                id: column.name,
                header: column.label,
                accessorKey: column.hasOwnProperty('columnKey') ? column.columnKey : column.name,
                enableSorting: column.hasOwnProperty('columnSorting') ? column.columnSorting : true,
                cell: (props => {
                    // console.log(column.name, props.row.original[column.name])
                    return column.hasOwnProperty('columnFn') ? column.columnFn?.(props.row.original) : props.row.original[column.name]
                }),
                className: column?.columnClassName,
            }]

            if (column.hasOwnProperty('subfields'))
                subcolumns.push(...Object.values(column.subfields?.() || []).filter(field => field && field.column && hasPermission(field.permissions, field.hasOwnProperty('requireAllPermissions') ? field.requireAllPermissions : true)).map(subcolumn => ({
                    id: `${column.name}.${subcolumn.name}`,
                    header: `${column.label} - ${subcolumn.label}`,
                    // accessorKey: subcolumn.hasOwnProperty('columnKey') ? subcolumn.columnKey : column.name,
                    // enableSorting: subcolumn.hasOwnProperty('columnSorting') ? subcolumn.columnSorting : true,
                    enableSorting: false,
                    cell: (props => {
                        // console.log(subcolumn.name, props.row.original[subcolumn.name])
                        return subcolumn.hasOwnProperty('columnFn') ? subcolumn.columnFn?.(props.row.original?.[column.name]) : props.row.original?.[column.name]?.[subcolumn.name]
                    }),
                    className: subcolumn?.columnClassName,
                })))

            columns.push(...subcolumns)
        }

        return columns
    }

    const getTableColumnsDropdown = ({modelName, fields}) => {
        let columnsSettings = getColumnsSettings({modelName, fields})

        return <Dropdown closeOnItemClick={false} placement={'bottom-end'} renderTitle={<Button size="sm" icon={<LuColumns3/>} title={'ستون‌ها'} variant={'default'}/>}>
            {Object.values(fields).filter(field => field && field.column && hasPermission(field.permissions, field.hasOwnProperty('requireAllPermissions') ? field.requireAllPermissions : true)).map(field => {
                const renderTitle = <Dropdown.Item key={field.name} onClick={() => dispatch(toggleColumn({modelName, columnName: field.name}))} disabled={field.columnFixed}><Checkbox checked={!columnsSettings[field.name]?.hidden} size={4} disabled={field.columnFixed}/> {field.label}</Dropdown.Item>
                if (field.hasOwnProperty('subfields'))
                    return <Dropdown.Menu key={`column-${field.name}`} submenuClass={'!left-full !right-[unset]'} className={"px-0"} placement={'top-end'} title={renderTitle}>{Object.values(field.subfields?.() || []).filter(field => field && field.column && hasPermission(field.permissions, field.hasOwnProperty('requireAllPermissions') ? field.requireAllPermissions : true)).map(subfield => (
                        <Dropdown.Item key={`column-${field.name}.${subfield.name}`} onClick={() => dispatch(toggleColumn({modelName, columnName: `${field.name}.${subfield.name}`}))}><Checkbox checked={!columnsSettings[`${field.name}.${subfield.name}`]?.hidden} size={4}/> {subfield.label}</Dropdown.Item>
                    ))}</Dropdown.Menu>

                return renderTitle

            })}
        </Dropdown>
    }

    const columnsSettings = useAppSelector(state => state.base.tables?.columnsSettings || {})
    const getColumnsSettings = useCallback(({modelName, fields}) => {
        let modelColumnsSettings = columnsSettings?.[modelName] || {}
        // let modelColumnsSettings =  {}

        let settingsToUpdate = {}
        for (let field of Object.values(fields)) {
            if (!hasPermission(field?.permissions, field?.hasOwnProperty('requireAllPermissions') ? field?.requireAllPermissions : true)) {
                continue
            }

            if (field.hasOwnProperty('columnHidden') && !modelColumnsSettings[field.name]?.hasOwnProperty('hidden'))
                settingsToUpdate[field.name] = {
                    ...modelColumnsSettings[field.name] || {},
                    hidden: field.columnHidden
                }

            if (!field.hasOwnProperty('subfields')) continue

            for (let subfield of Object.values(field?.subfields?.() || [])) {
                if (!hasPermission(subfield?.permissions, subfield?.hasOwnProperty('requireAllPermissions') ? subfield?.requireAllPermissions : true)) {
                    continue
                }

                if (!modelColumnsSettings[`${field.name}.${subfield.name}`]?.hasOwnProperty('hidden'))
                    settingsToUpdate[`${field.name}.${subfield.name}`] = {
                        ...modelColumnsSettings[`${field.name}.${subfield.name}`] || {},
                        hidden: true
                    }
            }
        }

        if (Object.keys(settingsToUpdate).length)
            // console.log(`setting columnsSettings of ${modelName}:`, modelColumnsSettings)
            dispatch(setColumnsSettings({
                modelName,
                columnsSettings: {
                    ...modelColumnsSettings,
                    ...settingsToUpdate
                }
            }))

        // console.log(`modelColumnsSettings:`, modelColumnsSettings)

        return modelColumnsSettings
    }, [columnsSettings])

    const getHiddenColumns = ({modelName, fields}) => {
        const columnsSettings = getColumnsSettings({modelName, fields})
        const hiddenColumns = Object.entries(columnsSettings)
            .map(([columnName, columnSettings]) => ({name: columnName, ...columnSettings}))
            .filter(columnSettings => columnSettings.hidden)
            .map(columnSettings => columnSettings.name)
        return hiddenColumns
    }


    const getFields = (fields) => {
        // let newFields = {}
        for (let key of Object.keys(fields)) {
            let field = fields[key]
            if (!hasPermission(field.permissions, field.hasOwnProperty('requireAllPermissions') ? field.requireAllPermissions : true))
                delete fields[key]

            // newFields[key] = field
        }

        return fields
    }

    useAutoLogout()
    useAutoLogoutOnTabClose();


    return (
        <SessionContext.Provider
            value={{
                sessionId,
                sessionData,
                setSessionData,
                socket,
                hasPermission,
                addEventListener,
                removeEventListener,
                getTableColumns,
                getTableColumnsDropdown,
                getColumnsSettings,
                getHiddenColumns,
                getFields,
            }}
        >
            {children}
        </SessionContext.Provider>
    )
}

function useSession() {
    const context = useContext(SessionContext)
    if (context === undefined)
        throw new Error('SessionContext was used outside of SessionProvider')
    return context
}

export {SessionProvider, useSession}
